import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { gsap, prefersReducedMotion } from 'lib/motion'

const Layer = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  overflow: hidden;
  pointer-events: none;
  contain: layout paint;
`

/**
 * The falloff is baked into the radial-gradient instead of applied with
 * `filter: blur()` — a blurred layer this large is re-rasterised every frame,
 * a gradient is not, so only the compositor does work while it drifts.
 */
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  will-change: transform;
  transform: translateZ(0);
`

const OrbViolet = styled(Orb)`
  top: -22vh;
  right: -14vw;
  width: 62vw;
  height: 62vw;
  max-width: 900px;
  max-height: 900px;
  background: radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.3), rgba(139, 92, 246, 0) 62%);
`

const OrbIndigo = styled(Orb)`
  bottom: -26vh;
  left: -16vw;
  width: 58vw;
  height: 58vw;
  max-width: 820px;
  max-height: 820px;
  background: radial-gradient(circle at 50% 50%, rgba(109, 40, 217, 0.28), rgba(109, 40, 217, 0) 62%);
`

const OrbCyan = styled(Orb)`
  top: 34vh;
  left: 42vw;
  width: 36vw;
  height: 36vw;
  max-width: 520px;
  max-height: 520px;
  background: radial-gradient(circle at 50% 50%, rgba(49, 208, 170, 0.14), rgba(49, 208, 170, 0) 66%);
`

/**
 * Slow-drifting aurora behind the app. Transform-only so it never triggers
 * layout, and it renders as a static gradient under prefers-reduced-motion.
 */
const AmbientBackground = () => {
  const scope = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!scope.current || prefersReducedMotion()) return undefined

    const ctx = gsap.context((self) => {
      const orbs = self.selector?.('[data-orb]') as HTMLElement[]
      orbs?.forEach((orb, i) => {
        gsap.to(orb, {
          xPercent: i % 2 === 0 ? 9 : -11,
          yPercent: i % 2 === 0 ? -7 : 8,
          scale: 1.12,
          duration: 18 + i * 5,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
        })
      })
    }, scope)

    return () => ctx.revert()
  }, [])

  return (
    <Layer ref={scope} aria-hidden>
      <OrbViolet data-orb />
      <OrbIndigo data-orb />
      <OrbCyan data-orb />
    </Layer>
  )
}

export default AmbientBackground
